import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Send, Pencil } from 'lucide-react';
import {
  enviarAltaGrupoLegajo,
  enviarAltaGruposLote,
  listarRechazados,
  reconciliarRechazados,
  type RechazadoItem,
  type ReconciliacionResultado,
} from '@lib/services/altas-masivas.service';
import { EnvioLoteModal } from '@components/EnvioLoteModal';
import { RechazoBinary } from '@components/RechazoBinary';
import { leerRechazoBinary } from '@lib/rechazo-binary';
import { getAsociado } from '@lib/services/observaciones.service';
import { updateAsociado } from '@lib/services/padrones.service';
import { EditAsociadoModal } from '@components/EditAsociadoModal';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import { useAuthStore } from '@lib/store/auth.store';
import { isMedicus, UserRole, type Asociado } from '@lib/types';
import { puedeVer as tieneVista } from '@lib/permissions/catalog';
import { useCanMutate } from '@lib/hooks/useCanMutate';
import { Link } from 'react-router-dom';
import { listarAdherentes, type Adherente } from '@lib/services/adherentes.service';

const PAGE_SIZE = 25;

function mensajeDeError(e: unknown): string {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message ?? err?.message ?? 'Ocurrió un error.';
}

/**
 * Bandeja de "Rechazados por Binary": legajos en `rechazado_binary` con el motivo
 * (ORA). El operador corrige el dato del titular (editar → PUT /asociados/:id) y
 * reenvía, o reconcilia contra Binary los que ya estaban afiliados (duplicados).
 */
export function RechazadosPage() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  // Gate por PERMISO DE VISTA, no por rol: `view:rechazados` deriva de
  // `view:legajos-listos`, asi que quien puede ver Alta Grupos ve tambien su
  // bandeja de rechazados. Con `isMedicus(rol)` el item aparecia en el menu y la
  // pantalla contestaba "No tenes permisos" (mismo bug que /legajos-listos).
  const puedeVer = user ? tieneVista(user, 'view:rechazados') : false;
  // Ver no es corregir: reenviar a Binary, editar el titular y reconciliar
  // siguen siendo de Medicus — los endpoints exigen ADMIN/SUPERADMIN.
  const puedeMutar = useCanMutate() && isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [editando, setEditando] = useState<Asociado | null>(null);
  const [recon, setRecon] = useState<ReconciliacionResultado | null>(null);
  const [reconLoading, setReconLoading] = useState(false);
  // Selección para el reenvío en lote. Se limita a la página visible a
  // propósito: una selección que sobrevive al paginado es invisible al enviar y
  // termina despachando altas a Binary que el operador ya no tiene a la vista.
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [enviandoLote, setEnviandoLote] = useState(false);

  const query = useQuery({
    queryKey: ['rechazados', page, q],
    queryFn: () => listarRechazados(page, PAGE_SIZE, q),
    enabled: puedeVer,
  });
  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / PAGE_SIZE));

  const items = query.data?.items ?? [];
  const idsPagina = items.map((it) => it.id);
  const seleccionados = idsPagina.filter((id) => seleccion.has(id));
  const todosMarcados = idsPagina.length > 0 && seleccionados.length === idsPagina.length;

  function refetch() {
    qc.invalidateQueries({ queryKey: ['rechazados'] });
    qc.invalidateQueries({ queryKey: ['legajos-listos-counts'] });
  }

  /** Cambiar de página o de búsqueda limpia la selección (ver nota arriba). */
  function irAPagina(p: number) {
    setSeleccion(new Set());
    setPage(p);
  }

  function toggleUno(id: string) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleTodos() {
    setSeleccion(todosMarcados ? new Set() : new Set(idsPagina));
  }

  async function abrirEdicion(asociadoId: string) {
    const a = await getAsociado(asociadoId);
    setEditando(a);
  }

  async function handleReconciliar() {
    setReconLoading(true);
    setRecon(null);
    try {
      const r = await reconciliarRechazados();
      setRecon(r);
      refetch();
    } catch (e) {
      setRecon({ revisados: 0, reconciliados: 0, detalle: [{ numero: '—', resultado: mensajeDeError(e) }] });
    } finally {
      setReconLoading(false);
    }
  }

  if (!puedeVer) {
    return (
      <section>
        <h1 className="heading-3">Rechazados</h1>
        <p className="text-element-mid">No tenés permisos para ver esta vista.</p>
      </section>
    );
  }

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Rechazados por Binary</h1>
        <p className="text-element-mid">
          Legajos que rebotaron en Binary, con el motivo que informó Binary. Corregí el dato del titular y{' '}
          <strong>reenviá</strong>, o <strong>reconciliá contra Binary</strong> los que rebotaron por “cuil ya existe”
          y en realidad ya estaban afiliados (duplicados).
        </p>
      </header>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="rowBetween" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="input"
            style={{ maxWidth: 320 }}
            placeholder="Buscar por legajo o motivo…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSeleccion(new Set());
              setPage(1);
            }}
            aria-label="Buscar rechazados"
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span className="heading-6" style={{ margin: 0 }}>
              {(query.data?.total ?? 0).toLocaleString('es-AR')} rechazados
            </span>
            {puedeMutar && seleccionados.length > 0 && (
              <button
                type="button"
                className="btn btn-primary btn--sm"
                onClick={() => setEnviandoLote(true)}
                title="Reenvía a Binary los legajos tildados, uno tras otro."
              >
                <Send size={14} /> Reenviar {seleccionados.length} seleccionado
                {seleccionados.length === 1 ? '' : 's'}
              </button>
            )}
            {puedeMutar && (
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                onClick={() => handleReconciliar()}
                disabled={reconLoading}
                title="Consulta PMED por CUIL y marca dado de alta los que ya estaban afiliados en Binary."
              >
                {reconLoading ? (
                  <>
                    <span className="spinner" /> Reconciliando…
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} /> Reconciliar contra Binary
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {recon && (
          <p className="status status-success" style={{ display: 'block', marginTop: 12 }}>
            Reconciliación: <strong>{recon.reconciliados}</strong> de {recon.revisados} marcados dado de alta
            {recon.reconciliados === 0 && recon.revisados > 0
              ? ' (ninguno estaba activo en Binary como cabeza; revisá los datos).'
              : '.'}
          </p>
        )}

        {query.isLoading && (
          <div style={{ marginTop: 16 }}>
            <TableSkeleton rows={8} />
          </div>
        )}
        {query.isError && (
          <p className="status status-error" style={{ display: 'block', marginTop: 16 }}>
            No se pudieron cargar los rechazados.
          </p>
        )}
        {query.data && query.data.items.length === 0 && (
          <p className="text-element-mid" style={{ marginTop: 16 }}>
            {q ? `No hay rechazados que coincidan con "${q}".` : 'No hay legajos rechazados por Binary. 🎉'}
          </p>
        )}

        {query.data && query.data.items.length > 0 && (
          <div className="tableWrap" style={{ marginTop: 16 }}>
            <table className="dataTable">
              <thead>
                <tr>
                  {puedeMutar && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={todosMarcados}
                        ref={(el) => {
                          if (el) el.indeterminate = seleccionados.length > 0 && !todosMarcados;
                        }}
                        onChange={toggleTodos}
                        aria-label="Seleccionar todos los de esta página"
                        title="Seleccionar todos los de esta página"
                      />
                    </th>
                  )}
                  <th>Legajo</th>
                  <th>Titular</th>
                  <th>Motivo del rechazo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <RechazadoRow
                    key={it.id}
                    item={it}
                    puedeMutar={puedeMutar}
                    seleccionado={seleccion.has(it.id)}
                    onToggle={() => toggleUno(it.id)}
                    onEditar={() => abrirEdicion(it.titular.asociadoId)}
                    onDone={refetch}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {query.data && query.data.total > PAGE_SIZE && (
          <footer className="rowBetween" style={{ alignItems: 'center', marginTop: 16 }}>
            <span className="text-element-mid" style={{ fontSize: 12 }}>
              Página {page} de {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page <= 1}
                onClick={() => irAPagina(Math.max(1, page - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page >= totalPages}
                onClick={() => irAPagina(Math.min(totalPages, page + 1))}
              >
                Siguiente
              </button>
            </div>
          </footer>
        )}
      </div>

      <IntegrantesRechazados />

      {enviandoLote && (
        <EnvioLoteModal
          titulo="Reenviar a Binary · selección"
          cantidad={seleccionados.length}
          confirmacion={
            <p style={{ margin: 0 }}>
              Vas a reenviar a Binary <strong>{seleccionados.length}</strong> legajo
              {seleccionados.length === 1 ? '' : 's'} rechazado
              {seleccionados.length === 1 ? '' : 's'}. Se despachan de a uno; los que transformen
              quedan <strong>dados de alta</strong> y los que vuelvan a rebotar siguen acá con el
              motivo nuevo. Si el rechazo era por un dato mal cargado y todavía no lo corregiste, va
              a rebotar igual.
            </p>
          }
          iniciar={() => enviarAltaGruposLote({ legajoIds: seleccionados })}
          onCerrar={() => {
            setEnviandoLote(false);
            setSeleccion(new Set());
            refetch();
          }}
          onFin={refetch}
        />
      )}

      {editando && (
        <EditAsociadoModal
          asociado={editando}
          onClose={() => setEditando(null)}
          onSave={async (patch) => {
            await updateAsociado(editando.id, patch);
            setEditando(null);
            refetch();
          }}
        />
      )}
    </section>
  );
}

function RechazadoRow(props: {
  item: RechazadoItem;
  puedeMutar: boolean;
  seleccionado: boolean;
  onToggle: () => void;
  onEditar: () => void;
  onDone: () => void;
}) {
  const { item, puedeMutar, seleccionado, onToggle, onEditar, onDone } = props;
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function reenviar() {
    setEnviando(true);
    setMsg(null);
    try {
      const r = await enviarAltaGrupoLegajo(item.id);
      if (r.estado === 'dado_de_alta') {
        setMsg({ ok: true, text: `✓ Dado de alta (MEDICARD ${r.integrantes?.[0]?.medicard ?? '—'})` });
      } else if (r.estado === 'omitido') {
        setMsg({ ok: true, text: 'Ya estaba dado de alta.' });
      } else {
        setMsg({
          ok: false,
          text: leerRechazoBinary(r.binaryError)?.motivo ?? r.errores?.join(' · ') ?? 'Sigue rechazado.',
        });
      }
      onDone();
    } catch (e) {
      setMsg({ ok: false, text: mensajeDeError(e) });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <tr>
      {puedeMutar && (
        <td>
          <input
            type="checkbox"
            checked={seleccionado}
            onChange={onToggle}
            aria-label={`Seleccionar legajo ${item.numero}`}
          />
        </td>
      )}
      <td>
        <strong>{item.numero}</strong>
        <div className="text-element-mid" style={{ fontSize: 11 }}>
          {item.integrantes.length} integrante{item.integrantes.length === 1 ? '' : 's'}
        </div>
      </td>
      <td>
        {item.titular.apellSolt} {item.titular.nombres}
        <div className="text-element-mid" style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
          DNI {item.titular.documento} · CUIL {item.titular.cuil ?? '—'}
        </div>
      </td>
      <td style={{ fontSize: 12, maxWidth: 420 }}>
        <RechazoBinary mensaje={item.binaryError} />
        {msg && (
          <div
            className={msg.ok ? 'status status-success' : 'status status-error'}
            style={{ fontSize: 11, marginTop: 4, display: 'inline-block' }}
          >
            {msg.text}
          </div>
        )}
      </td>
      <td>
        {puedeMutar && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn btn-ghost btn--sm" onClick={onEditar} title="Editar los datos del titular">
              <Pencil size={13} /> Editar
            </button>
            <button type="button" className="btn btn-primary btn--sm" onClick={() => reenviar()} disabled={enviando}>
              {enviando ? (
                <>
                  <span className="spinner" /> …
                </>
              ) : (
                <>
                  <Send size={13} /> Reenviar
                </>
              )}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

/**
 * Alta Integrantes rechazados por Binary (VEN-561). Los bis no cambian de
 * estado cuando Binary rebota (siguen pendientes y se reintentan), así que no
 * entran en la bandeja de arriba; acá se listan con el motivo para que la
 * organización sepa qué corregir. La acción sigue en Alta Integrantes.
 */
function IntegrantesRechazados() {
  const query = useQuery({
    queryKey: ['adherentes', 'rechazados'],
    queryFn: () => listarAdherentes({ page: 1, pageSize: 100, soloRechazados: true }),
  });
  const items: Adherente[] = query.data?.items ?? [];
  if (query.isLoading || query.isError || items.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="rowBetween" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 className="heading-5" style={{ margin: 0 }}>
          Alta Integrantes rechazados por Binary{' '}
          <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--color-element-mid)' }}>
            ({(query.data?.totalRechazados ?? items.length).toLocaleString('es-AR')})
          </span>
        </h2>
        <Link to="/legajos-bis" className="btn btn-ghost btn--sm">
          Ir a Alta Integrantes
        </Link>
      </div>
      <p className="text-element-mid" style={{ fontSize: 13, marginTop: 4 }}>
        Integrantes a sumar a un legajo ya afiliado que Binary rebotó. Siguen pendientes: corregí el
        dato desde Alta Integrantes y volvé a enviar (el envío automático también los reintenta cada
        noche).
      </p>
      {items.length > 0 && (
        <div className="tableWrap" style={{ marginTop: 12 }}>
          <table className="dataTable">
            <thead>
              <tr>
                <th>Legajo</th>
                <th>Integrante(s)</th>
                <th>Titular</th>
                <th>Motivo del rechazo</th>
                <th>Último intento</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>
                    <strong>{it.numero}</strong>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {it.adicionales.map((ad) => (
                      <div key={ad.asociadoId}>
                        {ad.apellSolt} {ad.nombres}
                        {ad.documento ? ` · DNI ${ad.documento}` : ''}
                      </div>
                    ))}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {it.titularApellido} {it.titularNombre}
                    <div className="text-element-mid" style={{ fontSize: 11 }}>
                      DNI {it.titularDocumento}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'normal', maxWidth: 420 }}>
                    <RechazoBinary mensaje={it.binaryError?.message} />
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
                    {it.binaryError?.at ? new Date(it.binaryError.at).toLocaleString('es-AR') : '—'}
                    {it.binaryError?.intentos && it.binaryError.intentos > 1
                      ? ` · ${it.binaryError.intentos} intentos`
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
